/* ----------------------------------------------------------------------------
<media-zoom>: shows an image or video large in an overlay; the backdrop,
Escape, or (for media without controls) another click closes it.

- an image, or a video without controls (a silent loop): click it, or focus it
  and press Enter or Space, to zoom
    <media-zoom><img alt="…"></media-zoom>
    <media-zoom><div class="video"><video autoplay muted loop playsinline aria-label="…">…</video></div></media-zoom>
- a video with controls: a play button (video.css) stands in for the controls,
  and plays it zoomed
    <media-zoom><div class="video"><video controls aria-label="…">…</video></div></media-zoom>

Without JavaScript, images stay as they are and videos keep their controls.
With reduced motion, silent loops don't play; zooming one shows its controls.
---------------------------------------------------------------------------- */

const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

export default class MediaZoom extends HTMLElement {
	static #active = null;
	static #listening = false;

	static #listen() {
		if (MediaZoom.#listening) return;
		MediaZoom.#listening = true;
		document.addEventListener('keydown', (e) => {
			if (e.key === 'Escape' && MediaZoom.#active) MediaZoom.#active.close();
		});
	}

	constructor() {
		super();
		this.handleClick = this.handleClick.bind(this);
		this.handleKeydown = this.handleKeydown.bind(this);
	}

	connectedCallback() {
		MediaZoom.#listen();
		this.video = this.querySelector('video');
		this.isPlayer = this.video?.hasAttribute('controls') ?? false;

		if (this.isPlayer) {
			// the play button replaces the native controls until the video is zoomed
			this.video.controls = false;
			this.video.tabIndex = -1;
			this.playButton = document.createElement('button');
			this.playButton.type = 'button';
			this.playButton.className = 'video__play';
			this.playButton.setAttribute(
				'aria-label',
				`Play ${this.label('the video')}`,
			);
			this.playButton.addEventListener('click', () => this.open());
			this.video.after(this.playButton);
		} else {
			// the element itself is the control: focusable, and named after its media
			this.tabIndex = 0;
			this.setAttribute('role', 'button');
			this.setAttribute('aria-label', `Zoom: ${this.label('media')}`);
			this.addEventListener('keydown', this.handleKeydown);

			if (this.video && reducedMotion.matches) {
				this.video.autoplay = false;
				this.video.pause();
			}
		}

		this.addEventListener('click', this.handleClick);
	}

	disconnectedCallback() {
		this.removeEventListener('click', this.handleClick);
		this.removeEventListener('keydown', this.handleKeydown);
		if (MediaZoom.#active === this) this.close();
	}

	// the media's own name: the image's alt text, or the video's aria-label
	label(fallback) {
		const media = this.video ?? this.querySelector('img');
		return (
			media?.getAttribute('aria-label') ||
			media?.getAttribute('alt') ||
			fallback
		);
	}

	handleClick(e) {
		if (!this.classList.contains('--active')) {
			if (!this.isPlayer) this.open();
			return;
		}
		// while zoomed, clicks on a video's controls are for the video; the backdrop always closes
		if (e.target === this || !this.video?.controls) this.close();
	}

	handleKeydown(e) {
		if (e.target !== this || (e.key !== 'Enter' && e.key !== ' ')) return;
		e.preventDefault();
		this.classList.contains('--active') ? this.close() : this.open();
	}

	open() {
		if (MediaZoom.#active) return; // one zoom at a time
		this.savedScrollY = window.scrollY;
		document.documentElement.classList.add('--zoomed');
		this.classList.add('--active');
		MediaZoom.#active = this;

		// the top layer shows the overlay above everything, whatever z-index and
		// stacking the page's headers and containers have; only while it's open,
		// since a closed popover is hidden
		if (this.showPopover) {
			this.popover = 'manual';
			this.showPopover();
		}

		if (this.isPlayer) {
			this.playButton.hidden = true;
			this.video.controls = true;
			this.video.focus({ preventScroll: true });
			this.video.play().catch(() => {}); // if the browser blocks it, the controls are there
		} else if (this.video && reducedMotion.matches) {
			this.video.controls = true; // a loop that doesn't play on its own, the visitor can start
		}
	}

	close() {
		if (this.hasAttribute('popover')) {
			this.hidePopover();
			this.removeAttribute('popover');
		}
		document.documentElement.classList.remove('--zoomed');
		this.classList.remove('--active');
		this.scrollTop = 0;
		if (MediaZoom.#active === this) MediaZoom.#active = null;

		if (this.isPlayer) {
			this.video.pause();
			this.video.controls = false;
			this.playButton.hidden = false;
			this.playButton.focus({ preventScroll: true });
		} else {
			if (this.video && reducedMotion.matches) {
				this.video.pause();
				this.video.controls = false;
			}
			this.focus({ preventScroll: true });
		}

		if (this.savedScrollY != null) window.scrollTo(0, this.savedScrollY);
	}
}

window.customElements.define('media-zoom', MediaZoom);
